import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Theme,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Inventory2Outlined as InventoryIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { OrderProductInventoryAvailability } from "../../../../../models/StockIssueRequest/OrderInventoryAvailability";
import {
  formatWarehouseLabel,
  formatWarehouseStockSummary,
} from "../../../helpers/orderInventoryAvailability.helper";
import {
  getModernPageColors,
  modernSectionPaperSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTableRowSx,
} from "../../../../../styles/modernListingPageSx";

interface OrderProductInventoryAvailabilityPopupProps {
  open: boolean;
  onClose: () => void;
  productName?: string;
  availability: OrderProductInventoryAvailability | null;
  isSkuLoading?: boolean;
  resolveWarehouseName?: (warehouseId: string) => string;
}

const SummaryStat: React.FC<{
  label: string;
  value: string | number;
  theme: Theme;
  accent?: "default" | "success" | "warning" | "error";
}> = ({ label, value, theme, accent = "default" }) => {
  const colors = getModernPageColors(theme);

  const valueColor =
    accent === "success"
      ? colors.success
      : accent === "warning"
        ? theme.palette.warning.dark
        : accent === "error"
          ? colors.error
          : colors.title;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 120,
        p: 1.75,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
      }}
    >
      <CustomTypography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.5,
          color: colors.subtitle,
          fontWeight: 600,
          fontSize: "0.68rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="heading6"
        fontWeight={700}
        sx={{ color: valueColor, fontSize: "1.25rem", lineHeight: 1.2 }}
      >
        {value}
      </CustomTypography>
    </Box>
  );
};

const OrderProductInventoryAvailabilityPopup: React.FC<
  OrderProductInventoryAvailabilityPopupProps
> = ({
  open,
  onClose,
  productName,
  availability,
  isSkuLoading = false,
  resolveWarehouseName,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const skuVariants = availability?.sku_variants ?? [];
  const hasSkuBreakdown = skuVariants.length > 0;

  const skuOrderedTotal = useMemo(
    () =>
      skuVariants.reduce(
        (sum, variant) => sum + Number(variant.ordered_quantity ?? 0),
        0,
      ),
    [skuVariants],
  );

  const orderLineQty = availability?.ordered_quantity ?? 0;
  const totalAvailable = availability?.total_available_quantity ?? 0;
  const hasShortfall = totalAvailable < orderLineQty;
  const hasSkuShortfall = skuVariants.some(
    (variant) =>
      Number(variant.available_quantity ?? 0) <
      Number(variant.ordered_quantity ?? 0),
  );
  const productWarehouseStock = availability?.warehouse_stock ?? [];

  const formatSkuWarehouses = (
    locations: OrderProductInventoryAvailability["sku_variants"][number]["warehouse_locations"],
  ) => {
    if (!locations?.length) return "—";
    return locations
      .map(
        (loc) =>
          `${formatWarehouseLabel(loc, resolveWarehouseName)} · ${loc.quantity}`,
      )
      .join(", ");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                backgroundColor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InventoryIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box minWidth={0}>
              <CustomTypography variant="heading6" fontWeight={700} sx={{ mb: 0.5 }}>
                Swageazy Inventory Availability
              </CustomTypography>
              <CustomTypography
                variant="body2"
                sx={{ color: colors.subtitle, mt: 0.25 }}
              >
                {productName || "Product"}
                {availability?.product_id
                  ? ` · #${availability.product_id}`
                  : ""}
              </CustomTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, bgcolor: colors.pageBg }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <SummaryStat
            theme={theme}
            label="Total available"
            value={totalAvailable}
            accent={
              hasShortfall ? "warning" : totalAvailable > 0 ? "success" : "default"
            }
          />
          <SummaryStat
            theme={theme}
            label="Order line qty"
            value={orderLineQty}
          />
          {hasSkuBreakdown ? (
            <SummaryStat
              theme={theme}
              label="SKU lines ordered"
              value={skuOrderedTotal}
            />
          ) : null}
          <Chip
            size="small"
            label={
              hasShortfall
                ? "Insufficient at product level"
                : hasSkuShortfall
                  ? "Some SKUs short"
                  : "Sufficient stock"
            }
            sx={{
              fontWeight: 600,
              height: 28,
              backgroundColor: hasShortfall || hasSkuShortfall ? "#FEF3C7" : "#DCFCE7",
              color: hasShortfall || hasSkuShortfall ? "#92400E" : "#166534",
            }}
          />
        </Box>

        {productWarehouseStock.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <CustomTypography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                color: colors.subtitle,
                fontWeight: 600,
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Available by warehouse
            </CustomTypography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {productWarehouseStock.map((loc) => (
                <Chip
                  key={loc.warehouse_id}
                  size="small"
                  label={`${formatWarehouseLabel(loc, resolveWarehouseName)} · ${loc.quantity}`}
                  sx={{
                    fontWeight: 600,
                    height: 28,
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : null}

        {hasSkuBreakdown &&
          skuOrderedTotal > 0 &&
          skuOrderedTotal !== orderLineQty ? (
          <CustomTypography
            variant="caption"
            sx={{
              display: "block",
              mb: 1.5,
              color: colors.subtitle,
              fontSize: "0.75rem",
            }}
          >
            SKU breakdown covers {skuOrderedTotal} of {orderLineQty} units on
            this order line. Remaining qty may be unallocated to variants.
          </CustomTypography>
        ) : null}

        {isSkuLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 5,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : hasSkuBreakdown ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              ...modernSectionPaperSx(theme),
              p: 0,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={modernTableHeaderCellSx(theme)}
                    fontSize="12px"
                    txtAlign="left"
                    title="SKU"
                  />
                  <AppTableHeaderCell
                    sx={modernTableHeaderCellSx(theme)}
                    fontSize="12px"
                    title="Variant"
                  />
                  <AppTableHeaderCell
                    sx={modernTableHeaderCellSx(theme)}
                    fontSize="12px"
                    title="Ordered"
                  />
                  <AppTableHeaderCell
                    sx={modernTableHeaderCellSx(theme)}
                    fontSize="12px"
                    title="Available"
                  />
                  <AppTableHeaderCell
                    sx={modernTableHeaderCellSx(theme)}
                    fontSize="12px"
                    title="Warehouses"
                  />
                  <AppTableHeaderCell
                    sx={modernTableHeaderCellSx(theme)}
                    fontSize="12px"
                    title="Status"
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {skuVariants.map((variant) => {
                  const ordered = Number(variant.ordered_quantity ?? 0);
                  const available = Number(variant.available_quantity ?? 0);
                  const isShort = available < ordered;

                  return (
                    <TableRow
                      key={`${variant.sku}-${variant.variant_id ?? "na"}`}
                      hover
                      sx={{
                        ...modernTableRowSx(theme),
                        ...(isShort
                          ? { bgcolor: `${theme.palette.warning.main}10` }
                          : {}),
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          ...modernTableBodyCellSx(theme),
                          textAlign: "left",
                          fontWeight: 600,
                          maxWidth: 220,
                          wordBreak: "break-word",
                        }}
                      >
                        {variant.sku}
                      </AppTableRowCell>
                      <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                        {variant.title || "—"}
                      </AppTableRowCell>
                      <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                        {ordered}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{
                          ...modernTableBodyCellSx(theme),
                          fontWeight: 700,
                          color: isShort ? colors.error : colors.title,
                        }}
                      >
                        {available}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{
                          ...modernTableBodyCellSx(theme),
                          fontSize: "0.72rem",
                          maxWidth: 260,
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {formatSkuWarehouses(variant.warehouse_locations)}
                      </AppTableRowCell>
                      <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                        <Chip
                          size="small"
                          label={isShort ? "Short" : "OK"}
                          sx={{
                            height: 22,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            backgroundColor: isShort ? "#FEE2E2" : "#DCFCE7",
                            color: isShort ? "#991B1B" : "#166534",
                          }}
                        />
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px dashed ${colors.border}`,
              backgroundColor: colors.surface,
              textAlign: "center",
            }}
          >
            <CustomTypography variant="body2" sx={{ color: colors.subtitle }}>
              No SKU variants on this order line. Product-level availability is
              shown in the summary above
              {productWarehouseStock.length > 0
                ? ` (${formatWarehouseStockSummary(
                    productWarehouseStock,
                    resolveWarehouseName,
                  )}).`
                : "."}
            </CustomTypography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderProductInventoryAvailabilityPopup;
